import { useEffect, useState } from 'react';
import { Input } from '../../../shared/components/Input';
import Button from '../../../shared/components/Button';
import type { BookingSettings, UpdateBookingSettingsPayload } from '../api/bookingServicesApi';

interface Props {
  settings: BookingSettings | undefined;
  onSubmit: (values: UpdateBookingSettingsPayload) => Promise<void> | void;
  isSaving: boolean;
  disabled?: boolean;
}

// Sensible platform defaults for a merchant that hasn't set booking rules yet.
const DEFAULTS = {
  timezone: 'Africa/Lagos',
  holdMinutes: '10', // holdTtlSeconds surfaced as minutes for a friendlier input
  minLeadHours: '2',
  cancellationNoticeHours: '24',
  rescheduleNoticeHours: '24',
  defaultBufferMins: '0',
};

const numOr = (v: string, fallback: number) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
};

/**
 * Merchant booking-rules form (PE-2100) — the writable half of "schedule configuration".
 * Edits the per-merchant GET/PATCH /bookings/settings fields. holdTtlSeconds is shown in
 * minutes for usability and converted back on submit.
 */
export function BookingSettingsForm({ settings, onSubmit, isSaving, disabled }: Props) {
  const [timezone, setTimezone] = useState(DEFAULTS.timezone);
  const [holdMinutes, setHoldMinutes] = useState(DEFAULTS.holdMinutes);
  const [minLeadHours, setMinLeadHours] = useState(DEFAULTS.minLeadHours);
  const [cancellationNoticeHours, setCancellationNoticeHours] = useState(DEFAULTS.cancellationNoticeHours);
  const [rescheduleNoticeHours, setRescheduleNoticeHours] = useState(DEFAULTS.rescheduleNoticeHours);
  const [defaultBufferMins, setDefaultBufferMins] = useState(DEFAULTS.defaultBufferMins);

  // Seed from the saved settings once they load.
  useEffect(() => {
    if (!settings) return;
    setTimezone(settings.timezone ?? DEFAULTS.timezone);
    setHoldMinutes(String(Math.round((settings.holdTtlSeconds ?? 600) / 60)));
    setMinLeadHours(String(settings.minLeadHours ?? DEFAULTS.minLeadHours));
    setCancellationNoticeHours(String(settings.cancellationNoticeHours ?? DEFAULTS.cancellationNoticeHours));
    setRescheduleNoticeHours(String(settings.rescheduleNoticeHours ?? DEFAULTS.rescheduleNoticeHours));
    setDefaultBufferMins(String(settings.defaultBufferMins ?? DEFAULTS.defaultBufferMins));
  }, [settings]);

  const handleSave = async () => {
    await onSubmit({
      timezone: timezone.trim() || DEFAULTS.timezone,
      holdTtlSeconds: numOr(holdMinutes, 10) * 60,
      minLeadHours: numOr(minLeadHours, 2),
      cancellationNoticeHours: numOr(cancellationNoticeHours, 24),
      rescheduleNoticeHours: numOr(rescheduleNoticeHours, 24),
      defaultBufferMins: numOr(defaultBufferMins, 0),
    });
  };

  return (
    <div className="bg-card rounded-2xl border border-border p-6">
      <div className="mb-4">
        <h2 className="text-[16px] font-semibold text-foreground">Booking rules</h2>
        <p className="text-[13px] text-muted-foreground mt-0.5">
          Schedule rules applied to every bookable service for this merchant.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          label="Timezone"
          placeholder="Africa/Lagos"
          value={timezone}
          disabled={disabled}
          onChange={(e) => setTimezone(e.target.value)}
        />
        <Input
          label="Payment hold (minutes)"
          type="number"
          min={1}
          value={holdMinutes}
          disabled={disabled}
          onChange={(e) => setHoldMinutes(e.target.value)}
        />
        <Input
          label="Minimum lead time (hours)"
          type="number"
          min={0}
          value={minLeadHours}
          disabled={disabled}
          onChange={(e) => setMinLeadHours(e.target.value)}
        />
        <Input
          label="Cancellation notice (hours)"
          type="number"
          min={0}
          value={cancellationNoticeHours}
          disabled={disabled}
          onChange={(e) => setCancellationNoticeHours(e.target.value)}
        />
        <Input
          label="Reschedule notice (hours)"
          type="number"
          min={0}
          value={rescheduleNoticeHours}
          disabled={disabled}
          onChange={(e) => setRescheduleNoticeHours(e.target.value)}
        />
        <Input
          label="Default buffer between bookings (minutes)"
          type="number"
          min={0}
          step={5}
          value={defaultBufferMins}
          disabled={disabled}
          onChange={(e) => setDefaultBufferMins(e.target.value)}
        />
      </div>
      <p className="text-[12px] text-muted-foreground mt-2">
        Applied to every service unless a service sets its own buffer.
      </p>

      {!disabled && (
        <div className="flex justify-end mt-5">
          <Button size="sm" className="h-[40px]" type="button" onClick={handleSave} loading={isSaving}>
            Save rules
          </Button>
        </div>
      )}
    </div>
  );
}
